import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ElementRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { GoogleCharts } from 'google-charts';
import { v4 as UUID } from 'uuid';

export interface ChartConfig {
  title: string;
  type: 'Pie' | 'Line' | 'Bar';
  is3D: boolean;
  legend?: {
    position: 'top' | 'left' | 'right' | 'bottom';
    alignment?: 'center';
  };
  height?: number;
  width?: number;
  vAxis?: { title: string; ticks?: number[] };
  hAxis?: { title: string };
  isStacked?: boolean;
  colors?: string[];
}

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
})
export class ChartComponent implements OnInit, OnDestroy {
  chartId: string = UUID();
  charts: any;
  visualization: any;
  resizeObserver: ResizeObserver;
  selectListener: any;
  private chartInstance: any = null;
  @Input() chartConfig: ChartConfig;
  @Input() data!: any[][];
  @Output() onChartClick: EventEmitter<string> = new EventEmitter<string>();
  @Output() onhideChart: EventEmitter<void> = new EventEmitter<void>();
  isChartLoaded: boolean = false;
  hasDrawnOnce: boolean = false;

  constructor(private elRef: ElementRef) { }

  ngOnInit() {
    GoogleCharts.load(this.onGoogleChartsLoaded.bind(this));
  }

  ngOnDestroy() {
    try {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (
        this.selectListener &&
        GoogleCharts.api &&
        GoogleCharts.api.visualization &&
        GoogleCharts.api.visualization.events
      ) {
        GoogleCharts.api.visualization.events.removeListener(
          this.selectListener
        );
        this.selectListener = null;
      }
      this.chartInstance = null;
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  onGoogleChartsLoaded(): void {
    if (
      GoogleCharts.api &&
      GoogleCharts.api.charts &&
      GoogleCharts.api.visualization
    ) {
      this.charts = GoogleCharts.api.charts;
      this.visualization = GoogleCharts.api.visualization;
      this.charts.load('current', {
        packages: ['corechart'],
        callback: this.setChartLoaded.bind(this),
      });
      this.charts.setOnLoadCallback(() => {
        this.drawChart.bind(this)();
      });

      this.resizeObserver = new ResizeObserver(() => {
        if (this.isChartLoaded && this.visualization) {
          // Prevent duplicate call during initial load
          if (!this.hasDrawnOnce) {
            this.hasDrawnOnce = true;
            return;
          }
          this.drawChart();
        }
      });

      this.resizeObserver.observe(this.elRef.nativeElement);
    } else {
      console.error('GoogleCharts API is not loaded properly.');
    }
  }

  setChartLoaded() {
    this.isChartLoaded = true;
  }

  private getChartConstructor(): any {
    switch (this.chartConfig?.type) {
      case 'Line':
        return this.visualization.LineChart;
      case 'Pie':
        return this.visualization.PieChart;
      default:
        return this.visualization.ColumnChart;
    }
  }

  drawChart() {
    // Create DataTable with explicit column types to prevent Google Charts
    // from auto-detecting and reformatting date strings
    const dataTable = new this.visualization.DataTable();
    
    // Add columns based on first row (headers)
    if (this.data.length > 0) {
      this.data[0].forEach((header: any, index: number) => {
        if (header !== null && typeof header === 'object' && header.role) {
          dataTable.addColumn({ type: header.type ?? 'string', role: header.role });
        } else {
          dataTable.addColumn(index === 0 ? 'string' : 'number', header);
        }
      });

      // Add data rows (skip header row)
      for (let i = 1; i < this.data.length; i++) {
        dataTable.addRow(this.data[i]);
      }
    }
    
    const options: any = {
      height: this.chartConfig?.height || 338,
      responsive: true,
      chartArea: { height: '60%', width: '80%' },
      width: this.chartConfig?.width,
      legend: {
        ...this.chartConfig?.legend, alignment: 'left', position: 'top',
         textStyle: { fontSize: 14, fontName: 'Roboto' }
        ,maxLines: 3, 
      },
      is3D: this.chartConfig?.is3D,
      isStacked: this.chartConfig?.isStacked,
      focusTarget: 'category',
      colors: this.chartConfig?.colors,
      vAxis: {
        ...this.chartConfig?.vAxis,
        format: 'short',
        fontName: 'Roboto',

        minValue: 0,
      },
      hAxis: { 
        ...this.chartConfig?.hAxis, 
        fontName: 'Roboto'
      },
      fontName: 'Roboto',
      plugins: {
        legend: {
          position: 'top',
          labels: {
            font: {
              size: 7,
              family: 'vazir',
            },
          },
        },
      }
    };

    const container = document.getElementById(this.chartId);
    if (!container) {
      return;
    }

    if (this.chartConfig?.type === 'Line') {
      options.curveType = 'function';
      options.pointSize = 5;
    }

    // Reuse the chart instance to avoid destroy/recreate flicker on resize
    if (!this.chartInstance) {
      const chartCtor = this.getChartConstructor();
      this.chartInstance = new chartCtor(container);

      // Add the select listener only once
      this.selectListener = GoogleCharts.api.visualization.events.addListener(
        this.chartInstance,
        'select',
        () => {
          const selectedItem = this.chartInstance.getSelection()[0];
          if (selectedItem) {
            const date = dataTable.getValue(selectedItem.row, 0);
            const numCols = dataTable.getNumberOfColumns();
            const fullRowSelection = [];
            for (let col = 1; col < numCols; col++) {
              fullRowSelection.push({ row: selectedItem.row, column: col });
            }
            this.chartInstance.setSelection(fullRowSelection);
            this.onChartClick.emit(date);
          } else {
            this.onhideChart.emit();
          }
        }
      );
    }

    this.chartInstance.draw(dataTable, options);
  }
}


